/**
 * Category Tree Component
 *
 * Displays categories in a hierarchical tree structure.
 */

import { useState } from 'react';
import { CategoryTreeNode } from './CategoryTreeNode';
import type { CategoryTreeNode as CategoryTreeNodeType } from '@bstock/shared';

interface CategoryTreeProps {
  nodes: CategoryTreeNodeType[];
  onAddChild: (parentId: string) => void;
  onEdit: (category: CategoryTreeNodeType) => void;
}

export function CategoryTree({ nodes, onAddChild, onEdit }: CategoryTreeProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    // By default, expand first two levels
    const ids = new Set<string>();
    nodes.forEach((node) => {
      ids.add(node.id);
      node.children?.forEach((child) => ids.add(child.id));
    });
    return ids;
  });

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="divide-y divide-neutral-100">
      {nodes.map((node) => (
        <CategoryTreeNode
          key={node.id}
          node={node}
          depth={0}
          isExpanded={expandedIds.has(node.id)}
          expandedIds={expandedIds}
          onToggleExpand={toggleExpanded}
          onAddChild={onAddChild}
          onEdit={onEdit}
        />
      ))}
    </div>
  );
}
